import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { contactSupport } from '../lib/auth-client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'

const SUBJECT_MAX = 200
const MESSAGE_MAX = 5000

interface ContactSupportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function ContactSupportModal({ open, onOpenChange }: ContactSupportModalProps) {
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')

  const mutation = useMutation({
    mutationFn: (body: { subject: string; message: string }) => contactSupport(body),
  })

  useEffect(() => {
    if (!open) {
      setSubject('')
      setMessage('')
      mutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const canSubmit =
    subject.trim().length > 0 &&
    message.trim().length > 0 &&
    subject.length <= SUBJECT_MAX &&
    message.length <= MESSAGE_MAX &&
    !mutation.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    mutation.mutate({ subject: subject.trim(), message: message.trim() })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Contact support</DialogTitle>
            <DialogDescription>
              Send a message to the Unbreakable team. We'll reply to the email on your account.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="support-subject">Subject</Label>
            <Input
              id="support-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={SUBJECT_MAX}
              placeholder="Brief summary"
              disabled={mutation.isPending || mutation.isSuccess}
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="support-message">Message</Label>
            <Textarea
              id="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MESSAGE_MAX}
              rows={6}
              placeholder="What's going on? Include any details that might help us diagnose the issue."
              disabled={mutation.isPending || mutation.isSuccess}
              required
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/{MESSAGE_MAX}
            </p>
          </div>

          {mutation.isError && (
            <p className="text-sm text-destructive">
              {mutation.error instanceof Error ? mutation.error.message : 'Failed to send message.'}
            </p>
          )}

          {mutation.isSuccess && (
            <p className="text-sm text-emerald-600">Message sent — we'll get back to you soon.</p>
          )}

          <DialogFooter>
            {mutation.isSuccess ? (
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="bg-brand text-white hover:bg-brand-hover"
              >
                Done
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="bg-brand text-white hover:bg-brand-hover disabled:bg-brand/50"
                >
                  {mutation.isPending ? 'Sending...' : 'Send message'}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
